import { getBrandVatRate } from '../../../shared/utils/brandCurrency';
import { SAUDI_BRAND } from '../../../products/saudi/config/saudiBrandConfig';

/** Saudi VAT rate — live from settings cache when available. */
export function getSaudiVatRate(settings) {
  return getBrandVatRate(settings);
}

export function getSaudiVatRateLabel(settings) {
  return `${getSaudiVatRate(settings)}%`;
}

/** @deprecated use getSaudiVatRate() — brand fallback only */
export const SAUDI_VAT_RATE = SAUDI_BRAND.vatRate;
/** @deprecated use getSaudiVatRateLabel() */
export const SAUDI_VAT_RATE_LABEL = `${SAUDI_VAT_RATE}%`;

/** Zakat standard rate for Saudi entities — زكاة */
export const SAUDI_ZAKAT_RATE = 2.5;
export const SAUDI_ZAKAT_RATE_LABEL = `${SAUDI_ZAKAT_RATE}%`;

/** Default corporate income tax rate (non-oil/gas sectors) — ضريبة الدخل */
export const SAUDI_CIT_RATE = 20;
export const SAUDI_CIT_RATE_LABEL = `${SAUDI_CIT_RATE}%`;

/** Common withholding tax rates — ضريبة الاستقطاع */
export const SAUDI_WHT_RATES = {
  dividends: 5,
  royalties: 15,
  technical_services: 15,
  management_fees: 20,
  rent: 5,
  default: 15,
};

/** TRN (Tax Registration Number) — الرقم الضريبي */
export const TRN_LENGTH = 15;
export const TRN_PATTERN = /^\d{15}$/;

export function validateTRN(trn) {
  const cleaned = String(trn || '').replace(/\D/g, '');
  return TRN_PATTERN.test(cleaned);
}

export function formatTRN(trn) {
  const cleaned = String(trn || '').replace(/\D/g, '').slice(0, TRN_LENGTH);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
}

/** ZATCA e-invoicing phases */
export const ZATCA_EINVOICING = {
  phase1Key: 'modules.comptabilite.pages.saudiTax.zatca_phase1',
  phase2Key: 'modules.comptabilite.pages.saudiTax.zatca_phase2',
  phase1: 'المرحلة 1 — الإصدار والحفظ (فاتورة)',
  phase2: 'المرحلة 2 — الربط والاعتماد',
  portalUrl: 'https://zatca.gov.sa',
  vatReturnPortal: 'https://zatca.gov.sa/en/eServices/Pages/default.aspx',
};

/** VAT return filing periods per ZATCA */
export const VAT_FILING_PERIODS = [
  { value: 'monthly', labelKey: 'modules.comptabilite.pages.saudiTax.vat_period_monthly', labelAr: 'شهري', label: 'شهري — للمنشآت الكبيرة', descriptionKey: 'modules.comptabilite.pages.saudiTax.vat_period_monthly_desc' },
  { value: 'quarterly', labelKey: 'modules.comptabilite.pages.saudiTax.vat_period_quarterly', labelAr: 'ربع سنوي', label: 'ربع سنوي — للمنشآت الصغيرة والمتوسطة', descriptionKey: 'modules.comptabilite.pages.saudiTax.vat_period_quarterly_desc' },
];

/** Declaration type tabs — replaces Algerian G50/IBS/TAP/IRG */
export const SAUDI_DECLARATION_TYPES = [
  { id: 'vat', code: 'VAT', nameKey: 'modules.comptabilite.pages.saudiTax.decl_vat', nameAr: 'ضريبة القيمة المضافة', name: 'ضريبة القيمة المضافة', filingPeriod: 'monthly' },
  { id: 'zakat', code: 'ZAKAT', nameKey: 'modules.comptabilite.pages.saudiTax.decl_zakat', nameAr: 'زكاة', name: 'إقرار الزكاة', filingPeriod: 'annual' },
  { id: 'cit', code: 'CIT', nameKey: 'modules.comptabilite.pages.saudiTax.decl_cit', nameAr: 'ضريبة الدخل', name: 'ضريبة الدخل على الشركات', filingPeriod: 'annual' },
  { id: 'wht', code: 'WHT', nameKey: 'modules.comptabilite.pages.saudiTax.decl_wht', nameAr: 'ضريبة الاستقطاع', name: 'ضريبة الاستقطاع من المنبع', filingPeriod: 'monthly' },
];

/** ZATCA VAT return line items (Form VAT return structure) */
export function buildDefaultVatReturn(settings) {
  const rate = getSaudiVatRateLabel(settings);
  const rateNum = getSaudiVatRate(settings);
  return {
    title: 'إقرار ضريبة القيمة المضافة',
    titleAr: 'إقرار ضريبة القيمة المضافة',
    description: 'ضريبة المخرجات والمدخلات — إدخال يدوي',
    descriptionAr: 'ضريبة المخرجات والمدخلات — إدخال يدوي',
    dueDate: '',
    status: 'قيد الإعداد',
    sections: [
      {
        id: 'vat-output',
        title: 'ضريبة المخرجات',
        titleAr: 'ضريبة المخرجات',
        items: [
          { code: '1', description: `المبيعات الخاضعة للنسبة الأساسية ${rateNum}%`, descriptionAr: `المبيعات الخاضعة للنسبة الأساسية ${rateNum}%`, baseHT: 0, taux: rate, tva: 0 },
          { code: '2', description: 'المبيعات الخاضعة للنسبة الصفرية', descriptionAr: 'المبيعات الخاضعة للنسبة الصفرية', baseHT: 0, taux: '0%', tva: 0 },
          { code: '3', description: 'المبيعات المعفاة', descriptionAr: 'المبيعات المعفاة', baseHT: 0, taux: '0%', tva: 0 },
          { code: '4', description: 'الصادرات', descriptionAr: 'الصادرات', baseHT: 0, taux: '0%', tva: 0 },
        ],
      },
      {
        id: 'vat-input',
        title: 'ضريبة المدخلات',
        titleAr: 'ضريبة المدخلات',
        items: [
          { code: '11', description: 'مشتريات السلع والخدمات', descriptionAr: 'مشتريات السلع والخدمات', baseHT: 0, taux: rate, tva: 0 },
          { code: '12', description: 'الأصول الرأسمالية', descriptionAr: 'الأصول الرأسمالية', baseHT: 0, taux: rate, tva: 0 },
          { code: '13', description: 'ضريبة الاستيراد', descriptionAr: 'ضريبة الاستيراد', baseHT: 0, taux: rate, tva: 0 },
        ],
      },
      {
        id: 'recap',
        title: 'الملخص',
        titleAr: 'الملخص',
        totals: {
          tvaCollectee: 0,
          tvaDeductible: 0,
          creditReportePeriodePrecedente: 0,
          tvaNetteAVerser: 0,
          netAVerser: 0,
        },
      },
    ],
  };
}

/** Default Zakat declaration structure */
export function buildDefaultZakatReturn() {
  return {
    title: 'إقرار الزكاة',
    description: 'زكاة عروض التجارة والأصول — إدخال يدوي',
    descriptionAr: 'زكاة عروض التجارة والأصول — إدخال يدوي',
    dueDate: '',
    status: 'قيد الإعداد',
    zakatBase: 0,
    zakatRate: SAUDI_ZAKAT_RATE,
    zakatDue: 0,
    sections: [
      {
        id: 'assets',
        title: 'الأصول الزكوية',
        items: [
          { description: 'الأصول المتداولة', descriptionAr: 'الأصول المتداولة', amount: 0 },
          { description: 'الأصول الثابتة (صافي)', descriptionAr: 'الأصول الثابتة (صافي)', amount: 0 },
          { description: 'خصم: الالتزامات', descriptionAr: 'خصم: الالتزامات', amount: 0 },
        ],
      },
    ],
  };
}

/** Default CIT declaration structure */
export function buildDefaultCitReturn() {
  return {
    title: 'إقرار ضريبة الدخل على الشركات',
    description: 'ضريبة الدخل على الشركات — للمساهمين غير السعوديين/غير الخليجيين',
    descriptionAr: 'ضريبة الدخل على الشركات — للمساهمين غير السعوديين/غير الخليجيين',
    dueDate: '',
    status: 'قيد الإعداد',
    taxableIncome: 0,
    citRate: SAUDI_CIT_RATE,
    citDue: 0,
  };
}

/** Default withholding tax declaration */
export function buildDefaultWhtReturn() {
  return {
    title: 'إقرار ضريبة الاستقطاع من المنبع',
    description: 'ضريبة الاستقطاع على المدفوعات للأشخاص غير المقيمين',
    descriptionAr: 'ضريبة الاستقطاع على المدفوعات للأشخاص غير المقيمين',
    dueDate: '',
    status: 'قيد الإعداد',
    sections: [
      {
        id: 'payments',
        title: 'المدفوعات الخاضعة للاستقطاع',
        items: [
          { type: 'dividends', descriptionAr: 'أرباح الأسهم', rate: `${SAUDI_WHT_RATES.dividends}%`, base: 0, wht: 0 },
          { type: 'royalties', descriptionAr: 'الإتاوات', rate: `${SAUDI_WHT_RATES.royalties}%`, base: 0, wht: 0 },
          { type: 'technical_services', descriptionAr: 'الخدمات الفنية', rate: `${SAUDI_WHT_RATES.technical_services}%`, base: 0, wht: 0 },
          { type: 'management_fees', descriptionAr: 'رسوم الإدارة', rate: `${SAUDI_WHT_RATES.management_fees}%`, base: 0, wht: 0 },
        ],
      },
    ],
    totalWht: 0,
  };
}

/** SOCPA depreciation — useful life & straight-line rates (Arabic labels for Saudi UI) */
export const SOCPA_DEPRECIATION = {
  methods: [
    { id: 'straight_line', labelAr: 'القسط الثابت', labelKey: 'method_straight_line', soctaRef: 'IAS 16 / SOCPA' },
    { id: 'declining_balance', labelAr: 'القسط المتناقص', labelKey: 'method_declining_balance', soctaRef: 'IAS 16 / SOCPA' },
    { id: 'units_of_production', labelAr: 'وحدات الإنتاج', labelKey: 'method_units_of_production', soctaRef: 'IAS 16 / SOCPA' },
  ],
  categories: [
    { id: 'buildings', labelAr: 'المباني والمنشآت', labelKey: 'cat_buildings', usefulLifeYears: 25, defaultRate: 4 },
    { id: 'machinery', labelAr: 'الآلات والمعدات', labelKey: 'cat_machinery', usefulLifeYears: 10, defaultRate: 10 },
    { id: 'vehicles', labelAr: 'المركبات', labelKey: 'cat_vehicles', usefulLifeYears: 5, defaultRate: 20 },
    { id: 'furniture', labelAr: 'الأثاث والتجهيزات', labelKey: 'cat_furniture', usefulLifeYears: 5, defaultRate: 20 },
    { id: 'it_equipment', labelAr: 'أجهزة الحاسب والبرمجيات', labelKey: 'cat_it_equipment', usefulLifeYears: 4, defaultRate: 25 },
    { id: 'leasehold', labelAr: 'تحسينات العقارات المستأجرة', labelKey: 'cat_leasehold', usefulLifeYears: 10, defaultRate: 10 },
    { id: 'intangible', labelAr: 'الأصول غير الملموسة', labelKey: 'cat_intangible', usefulLifeYears: 5, defaultRate: 20 },
  ],
};

/** Saudi additional taxes (replaces Algerian IRA/TAP/FOPROLOS etc.) */
export const SAUDI_ADDITIONAL_TAXES = {
  overview: {
    exercice: '2025',
    totalTaxes: 4,
    completedTaxes: 1,
    pendingAmount: 125000,
    nextDeadline: '28/02/2026',
  },
  taxes: [
    {
      id: 'excise', code: 'EXCISE', category: 'excise',
      nom: 'الضريبة الانتقائية', nomAr: 'الضريبة الانتقائية',
      typeKey: 'modules.comptabilite.pages.saudiTax.type_excise', type: 'الضريبة الانتقائية', taux: 'متغيرة',
      deadline: '28/02/2026', montantDu: 45000, baseImposable: 900000,
      status: 'pending',
      reductions: [],
      details: { descriptionKey: 'modules.comptabilite.pages.saudiTax.excise_detail', description: 'ضريبة انتقائية على التبغ ومشروبات الطاقة وغيرها' },
    },
    {
      id: 'zakat', code: 'ZAKAT', category: 'zakat',
      nom: 'الزكاة', nomAr: 'الزكاة',
      typeKey: 'modules.comptabilite.pages.saudiTax.type_zakat', type: 'الزكاة', taux: `${SAUDI_ZAKAT_RATE}%`,
      deadline: '30/04/2026', montantDu: 85000, baseImposable: 3400000,
      status: 'in_progress',
      reductions: [],
      details: { zakatBase: 3400000, rate: SAUDI_ZAKAT_RATE },
    },
    {
      id: 'wht', code: 'WHT', category: 'wht',
      nom: 'ضريبة الاستقطاع', nomAr: 'ضريبة الاستقطاع من المنبع',
      typeKey: 'modules.comptabilite.pages.saudiTax.type_wht', type: 'ضريبة الاستقطاع من المنبع', taux: '5–20%',
      deadline: '10/03/2026', montantDu: 32000, baseImposable: 213000,
      status: 'completed',
      reductions: [],
      details: { paymentsCount: 12 },
    },
    {
      id: 'cit', code: 'CIT', category: 'cit',
      nom: 'ضريبة الدخل على الشركات', nomAr: 'ضريبة الدخل',
      typeKey: 'modules.comptabilite.pages.saudiTax.type_cit', type: 'ضريبة الدخل على الشركات', taux: `${SAUDI_CIT_RATE}%`,
      deadline: '30/06/2026', montantDu: 0, baseImposable: 0,
      status: 'pending',
      reductions: [],
      details: { noteKey: 'modules.comptabilite.pages.saudiTax.cit_note', note: 'تُطبق على حصة المساهمين غير السعوديين/غير الخليجيين' },
    },
  ],
  calendar: [
    { date: '10/03/2026', taxe: 'ضريبة الاستقطاع من المنبع', montant: 32000, status: 'completed', taxId: 'wht' },
    { date: '28/02/2026', taxe: 'الضريبة الانتقائية', montant: 45000, status: 'urgent', taxId: 'excise' },
    { date: '30/04/2026', taxe: 'إقرار الزكاة', montant: 85000, status: 'pending', taxId: 'zakat' },
    { date: '30/06/2026', taxe: 'ضريبة الدخل على الشركات', montant: 0, status: 'future', taxId: 'cit' },
  ],
  summary: { totalAnnuel: 162000, verse: 32000, restantDu: 130000 },
};

/** Liasse fiscale tableau 13 — Saudi tax summary (replaces TAP) */
export const SAUDI_LIASSE_TABLEAU_13_COLUMNS = [
  { key: 'designation', labelKey: 'col_designation', type: 'text', placeholderKey: 'ph_tax_type' },
  { key: 'base_imposable', labelKey: 'col_taxable_base', type: 'currency', placeholder: '0.00' },
  { key: 'taux', labelKey: 'col_rate', type: 'text', placeholder: '15%' },
  { key: 'montant_du', labelKey: 'col_tax_due', type: 'currency', placeholder: '0.00' },
];
